import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { User } from '../../../shared/services/users/users.service';
import { ManagerHouseholdsService } from '../../../shared/services/manager-households/manager-households.service';
import { ManagerHouseholdsResponse, ManagerHouseholdAssignment } from '../../../shared/services/manager-households/manager-households.model';
import { AdvisorHouseholdsService } from '../../../shared/services/advisor-households/advisor-households.service';
import { AdvisorHouseholdAssignment, AdvisorHouseholdsResponse } from '../../../shared/services/advisor-households/advisor-households.model';
import { HouseholdsService } from '../../../shared/services/households/households.service';
import { Household } from '../../../shared/models/households.model';
import { ToasterService } from '../../../shared/services/toaster/toaster.service';
import { UserRoleService } from '../../../shared/services/user-role/user-role.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-user-detail-households',
  templateUrl: './user-detail-households.component.html',
  styleUrls: ['./user-detail-households.component.scss']
})
export class UserDetailHouseholdsComponent implements OnInit, OnDestroy {

  @Input() user!: User;

  private subscriptions: Subscription[] = [];

  // Data properties
  assignments: ManagerHouseholdAssignment[] = [];
  total_count = 0;
  current_page = 1;
  page_size = 25;
  has_next_page = false;
  has_previous_page = false;

  // Computed pagination properties (matching application-users pattern)
  get totalItems(): number {
    return this.total_count;
  }

  get totalPages(): number {
    return Math.ceil(this.total_count / this.page_size) || 0;
  }

  get currentPage(): number {
    return this.current_page;
  }

  get pageSize(): number {
    return this.page_size;
  }

  get canManageAssignments(): boolean {
    return this.userRoleService.isManagerOrHigher();
  }

  // UI state
  isLoading = false;
  isLoadingMore = false;

  // Household assignment
  allHouseholds: Household[] = [];
  selectedHouseholdId: number | null = null;
  showHouseholdSearch = false;
  isAssigningHousehold = false;
  householdSearchQuery = '';
  noHouseholdResults = false;
  isSearchingHouseholds = false;
  isHouseholdSearchFocused = false;
  private householdSearchSubject = new Subject<string>();
  private householdSearchSubscription: Subscription | null = null;

  constructor(
    private managerHouseholdsService: ManagerHouseholdsService,
    private advisorHouseholdsService: AdvisorHouseholdsService,
    private householdsService: HouseholdsService,
    private toaster: ToasterService,
    private router: Router,
    private userRoleService: UserRoleService
  ) { }

  ngOnInit(): void {
    const role = this.user?.ROLE?.toUpperCase();
    if (role === 'MANAGER' || role === 'ADVISOR') {
      void this.loadHouseholdAssignments();
    }

    // Set up household search debouncing
    this.householdSearchSubscription = this.householdSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(query => {
      void this.loadAllHouseholds(query);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.householdSearchSubscription) {
      this.householdSearchSubscription.unsubscribe();
    }
  }

  public isViewingAdvisor(): boolean {
    return this.user?.ROLE?.toUpperCase() === 'ADVISOR';
  }

  /**
   * Map advisor API rows to the manager-shaped model used by the template.
   */
  private mapAdvisorAssignmentToManagerShape(a: AdvisorHouseholdAssignment): ManagerHouseholdAssignment {
    return {
      MANAGER_HOUSEHOLD_ID: a.ADVISOR_HOUSEHOLD_ID,
      MANAGER_ID: a.ADVISOR_ID,
      HOUSEHOLD_ID: a.HOUSEHOLD_ID,
      ASSIGNED_BY: a.ASSIGNED_BY,
      CREATED_AT: a.CREATED_AT,
      UPDATED_AT: a.UPDATED_AT,
      manager: {
        USER_ID: a.advisor?.USER_ID ?? a.ADVISOR_ID,
        EMAIL: a.advisor?.EMAIL ?? '',
        FIRST_NAME: a.advisor?.FIRST_NAME ?? '',
        LAST_NAME: a.advisor?.LAST_NAME ?? '',
        ROLE: a.advisor?.ROLE ?? 'ADVISOR'
      },
      household: {
        HOUSEHOLD_ID: a.household.HOUSEHOLD_ID,
        HOUSEHOLD_NAME: a.household.HOUSEHOLD_NAME ?? '',
        ORGANIZATION_ID: a.household.ORGANIZATION_ID ?? 0
      },
      assignedBy: {
        USER_ID: a.assignedBy.USER_ID,
        EMAIL: a.assignedBy.EMAIL,
        FIRST_NAME: a.assignedBy.FIRST_NAME ?? '',
        LAST_NAME: a.assignedBy.LAST_NAME ?? '',
        ROLE: a.assignedBy.ROLE ?? ''
      }
    };
  }

  /**
   * Load households assigned to this manager or advisor
   */
  private async loadHouseholdAssignments(page: number = 1): Promise<void> {
    if (!this.user) {
      return;
    }

    this.isLoading = page === 1;
    this.isLoadingMore = page > 1;

    try {
      const offset = (page - 1) * this.page_size;

      if (this.isViewingAdvisor()) {
        const response: AdvisorHouseholdsResponse = await this.advisorHouseholdsService.getAdvisorHouseholds(
          this.user.USER_ID,
          this.page_size,
          offset
        );
        const mapped = response.data.assignments.map(a => this.mapAdvisorAssignmentToManagerShape(a));
        if (page === 1) {
          this.assignments = mapped;
        } else {
          this.assignments = [...this.assignments, ...mapped];
        }
        this.total_count = response.data.total_count;
        this.current_page = response.data.page;
        this.page_size = response.data.page_size;
        this.has_next_page = response.data.has_next_page;
        this.has_previous_page = response.data.has_previous_page;
      } else {
        const response: ManagerHouseholdsResponse = await this.managerHouseholdsService.getManagerHouseholds(
          this.user.USER_ID,
          this.page_size,
          offset
        );

        if (page === 1) {
          this.assignments = response.data.assignments;
        } else {
          this.assignments = [...this.assignments, ...response.data.assignments];
        }

        this.total_count = response.data.total_count;
        this.current_page = response.data.page;
        this.page_size = response.data.page_size;
        this.has_next_page = response.data.has_next_page;
        this.has_previous_page = response.data.has_previous_page;
      }

    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.toaster.warning(`Error loading household assignments: ${message}`, 4000);
      console.error('Error loading household assignments:', error);
    } finally {
      this.isLoading = false;
      this.isLoadingMore = false;
    }
  }

  /**
   * Load more households (pagination) - keeping for backward compatibility
   */
  public async loadMore(): Promise<void> {
    if (!this.has_next_page || this.isLoadingMore) {
      return;
    }

    await this.loadHouseholdAssignments(this.current_page + 1);
  }

  /**
   * Pagination helpers and handlers (matching application-users pattern)
   */
  public get showingFrom(): number {
    if (this.totalItems === 0) {
      return 0;
    }
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  public get showingTo(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  public goToPage(page: number): void {
    if (page < 1) {
      return;
    }
    if (this.totalPages && page > this.totalPages) {
      return;
    }
    this.current_page = page;
    void this.loadHouseholdAssignments(page);
  }

  public nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  public prevPage(): void {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  public firstPage(): void {
    this.goToPage(1);
  }

  public lastPage(): void {
    if (this.totalPages) {
      this.goToPage(this.totalPages);
    }
  }

  public onPageSizeChange(size: number): void {
    if (!size || size < 1) {
      return;
    }
    this.page_size = size;
    this.current_page = 1;
    void this.loadHouseholdAssignments(1);
  }

  /**
   * Navigate to household detail view
   */
  public navigateToHousehold(householdId: number): void {
    this.router.navigate(['/households', householdId], {
      queryParams: {
        origin: 'user-detail',
        userId: this.user.USER_ID
      }
    });
  }

  /**
   * Remove a household assignment
   */
  public async removeHouseholdAssignment(assignment: ManagerHouseholdAssignment): Promise<void> {
    try {
      if (this.isViewingAdvisor()) {
        await this.advisorHouseholdsService.unassignAdvisorFromHousehold(
          assignment.MANAGER_ID,
          assignment.HOUSEHOLD_ID
        );
      } else {
        await this.managerHouseholdsService.unassignManagerFromHousehold(
          assignment.MANAGER_ID,
          assignment.HOUSEHOLD_ID
        );
      }

      // Remove from local array
      this.assignments = this.assignments.filter(a => a.MANAGER_HOUSEHOLD_ID !== assignment.MANAGER_HOUSEHOLD_ID);
      this.total_count--;

      this.toaster.success('Household assignment removed successfully');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.toaster.warning(`Error removing household assignment: ${message}`, 4000);
      console.error('Error removing household assignment:', error);
    }
  }

  /**
   * Format date for display
   */
  public formatDate(dateString: string): string {
    if (!dateString) {
      return '';
    }

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  /**
   * Get display name for a user
   */
  public getDisplayName(user: { FIRST_NAME: string; LAST_NAME: string }): string {
    return `${user.FIRST_NAME} ${user.LAST_NAME}`;
  }

  /**
   * Load all households for the assign household dropdown
   */
  private async loadAllHouseholds(query?: string): Promise<void> {
    this.isSearchingHouseholds = true;
    this.noHouseholdResults = false;
    try {
      const response = await this.householdsService.listHouseholds({
        page: 1,
        householdName: query,
        pageSize: 50 // Limit results for dropdown
      });
      this.allHouseholds = response.households;
      this.noHouseholdResults = !!query && query.length >= 3 && this.allHouseholds.length === 0;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.toaster.warning(`Error fetching households: ${message}`, 4000);
      console.error('Error fetching households:', error);
    } finally {
      this.isSearchingHouseholds = false;
    }
  }

  /**
   * Handle household search input
   */
  public onHouseholdSearch(query: string): void {
    if (query.length >= 3) {
      this.isSearchingHouseholds = true;
      this.householdSearchSubject.next(query);
    } else {
      this.allHouseholds = []; // Clear results if query is too short
      this.noHouseholdResults = false;
      this.isSearchingHouseholds = false;
    }
  }

  /**
   * Handle household search focus
   */
  public onHouseholdSearchFocus(): void {
    this.isHouseholdSearchFocused = true;
  }

  /**
   * Handle household search blur
   */
  public onHouseholdSearchBlur(): void {
    // Use a timeout to allow click events on the results to register
    setTimeout(() => {
      this.isHouseholdSearchFocused = false;
    }, 200);
  }

  /**
   * Select a household from search results
   */
  public selectHousehold(household: Household): void {
    this.selectedHouseholdId = household.HOUSEHOLD_ID;
    this.householdSearchQuery = household.HOUSEHOLD_NAME;
    this.allHouseholds = []; // Hide the results list
  }

  /**
   * Toggle household search form
   */
  public toggleHouseholdSearch(): void {
    this.showHouseholdSearch = !this.showHouseholdSearch;
    this.selectedHouseholdId = null;
    this.householdSearchQuery = '';
    this.allHouseholds = [];
  }

  /**
   * Assign a household to this manager or advisor
   */
  public async assignHousehold(): Promise<void> {
    if (!this.selectedHouseholdId) {
      this.toaster.warning('Please select a household to assign.', 4000);
      return;
    }

    this.isAssigningHousehold = true;
    try {
      if (this.isViewingAdvisor()) {
        await this.advisorHouseholdsService.assignAdvisorToHousehold(this.user.USER_ID, this.selectedHouseholdId);
      } else {
        await this.managerHouseholdsService.assignManagerToHousehold(this.user.USER_ID, this.selectedHouseholdId);
      }
      this.toaster.success('Household assigned successfully!', 4000);
      void this.loadHouseholdAssignments(1); // Refresh the list
      this.selectedHouseholdId = null; // Reset selection
      this.showHouseholdSearch = false; // Hide the search form
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.toaster.warning(`Error assigning household: ${message}`, 4000);
      console.error('Error assigning household:', error);
    } finally {
      this.isAssigningHousehold = false;
    }
  }
}
